import { closeUI } from "./memoryUI.js";
import { resetPlayerPos, resetLoadoutForCheckpointRestart } from "../systems/memory.js";
import { clearSave } from "../core/saveSystem.js";
import { resetHumanDialogueLines } from "./humanDialogueUI.js";
import { resetTutorialNarrationBeats } from "../systems/roomNarration.js";

/**
 * Pause & Escape System UI
 */

export const ESCAPE_UI_CONFIG = {
    modal: {
        width: 320,
        height: 200,
        backgroundColor: [25, 30, 40],
        borderColor: [60, 70, 90],
    },
    button: {
        width: 240,
        height: 38,
        resumeColor: [0, 200, 120],
        restartColor: [40, 120, 220],
        resetColor: [220, 50, 50],
        hoverOpacity: 0.9,
    },
    typography: {
        titleSize: 14,
        subtitleSize: 10,
        buttonTextSize: 12,
    },
};

let isOpen = false;
let eventsInitialized = false;
let eventHandles = [];
const ESCAPE_UI_Z = 9800;

function trackEventHandle(handle) {
    if (handle && typeof handle.cancel === "function") {
        eventHandles.push(handle);
    }
    return handle;
}

function initEscapeUIEvents() {
    if (eventsInitialized) return;
    eventsInitialized = true;
    trackEventHandle(onResize(() => {
        if (isOpen) {
            renderEscapeUI();
        }
    }));
}

export function isEscapeUIOpen() {
    return isOpen;
}

/**
 * Fresh KAPLAY scene scopes drop prior listeners — reset sticky init so
 * onResize rebinds on each game scene entry (same pattern as textbox UI).
 */
export function resetEscapeUIForScene() {
    closeEscapeUI();
    eventHandles.forEach((handle) => handle.cancel());
    eventHandles = [];
    eventsInitialized = false;
}

export function toggleEscapeUI() {
    isOpen = !isOpen;

    const canvas = document.querySelector("canvas");

    if (isOpen) {
        if (closeUI) closeUI();
        if (canvas) canvas.style.cursor = "default";
        renderEscapeUI();
    } else {
        closeEscapeUI();
    }
}

export function closeEscapeUI() {
    isOpen = false;
    destroyAll("escape_ui");
}

export function renderEscapeUI() {
    initEscapeUIEvents();
    destroyAll("escape_ui");
    if (!isOpen) return;

    const w = width();
    const h = height();

    const { modal, button, typography } = ESCAPE_UI_CONFIG;

    const modalX = (w - modal.width) / 2;
    const modalY = (h - modal.height) / 2;

    // Dark Background Overlay
    add([
        rect(w, h),
        pos(0, 0),
        color(0, 0, 0),
        opacity(0.65),
        fixed(),
        z(ESCAPE_UI_Z),
        "escape_ui",
    ]);

    // Modal Background Box
    add([
        rect(modal.width, modal.height, { radius: 6 }),
        pos(modalX, modalY),
        color(modal.backgroundColor[0], modal.backgroundColor[1], modal.backgroundColor[2]),
        outline(2, rgb(modal.borderColor[0], modal.borderColor[1], modal.borderColor[2])),
        fixed(),
        z(ESCAPE_UI_Z + 1),
        "escape_ui",
    ]);

    // Title
    add([
        text("PAUSED", { font: "ShareTechMono", size: typography.titleSize }),
        pos(w / 2, modalY + 24),
        anchor("center"),
        color(0, 255, 180),
        fixed(),
        z(ESCAPE_UI_Z + 2),
        "escape_ui",
    ]);

    // Resume Button Box
    const resumeBtn = add([
        rect(button.width, button.height, { radius: 4 }),
        pos(w / 2, modalY + 60),
        anchor("center"),
        color(button.resumeColor[0], button.resumeColor[1], button.resumeColor[2]),
        area({ isSensor: true }),
        fixed(),
        z(ESCAPE_UI_Z + 2),
        "escape_ui",
    ]);

    // Resume Button Text
    add([
        text("Resume Game", { font: "ShareTechMono", size: typography.buttonTextSize }),
        pos(w / 2, modalY + 60),
        anchor("center"),
        color(255, 255, 255),
        fixed(),
        z(ESCAPE_UI_Z + 3),
        "escape_ui",
    ]);

    resumeBtn.onClick(() => {
        closeEscapeUI();
    });

    // Restart from Checkpoint Button Box
    const restartBtn = add([
        rect(button.width, button.height, { radius: 4 }),
        pos(w / 2, modalY + 110),
        anchor("center"),
        color(button.restartColor[0], button.restartColor[1], button.restartColor[2]),
        area({ isSensor: true }),
        fixed(),
        z(ESCAPE_UI_Z + 2),
        "escape_ui",
    ]);

    // Restart from Checkpoint Button Text
    add([
        text("Restart from Checkpoint", { font: "ShareTechMono", size: typography.buttonTextSize }),
        pos(w / 2, modalY + 110),
        anchor("center"),
        color(255, 255, 255),
        fixed(),
        z(ESCAPE_UI_Z + 3),
        "escape_ui",
    ]);

    restartBtn.onClick(() => {
        closeUI();
        resetLoadoutForCheckpointRestart();
        isOpen = false;
        destroyAll("escape_ui");
        go("boot", {
            autoStart: true,
            resetMemory: false,
            kernelOnlyMemory: false,
            openMemoryOnInit: false,
            respawnFromCheckpoint: true,
        });
    });

    // Restart from Beginning Button Box
    const resetBtn = add([
        rect(button.width, button.height, { radius: 4 }),
        pos(w / 2, modalY + 160),
        anchor("center"),
        color(button.resetColor[0], button.resetColor[1], button.resetColor[2]),
        area({ isSensor: true }),
        fixed(),
        z(ESCAPE_UI_Z + 2),
        "escape_ui",
    ]);

    // Restart from Beginning Button Text
    add([
        text("Restart from Beginning", { font: "ShareTechMono", size: typography.buttonTextSize }),
        pos(w / 2, modalY + 160),
        anchor("center"),
        color(255, 255, 255),
        fixed(),
        z(ESCAPE_UI_Z + 3),
        "escape_ui",
    ]);

    resetBtn.onClick(() => {
        clearSave();
        resetPlayerPos();
        resetHumanDialogueLines();
        resetTutorialNarrationBeats();
        isOpen = false;
        destroyAll("escape_ui");
        go("intro");
    });
}
